import { ClientSideValidationError } from '@atlassian/jira-business-error-handling';
import { useNumberField } from '@atlassian/jira-issue-field-number';
import {
	NUMBER_CF_TYPE,
	STORY_POINTS_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
} from '@atlassian/jira-platform-field-config';
import { VALIDATION_ERROR_CODES } from '../../../clipboard-handler/constants';
import {
	assertClipboardHasPlainTextFormat,
	useClipboardHandler,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteHandler,
} from '../../../types';
import { createClipboardHelperComponent } from '../../utils';

const getPastedValue = (clipboardItem: ClipboardItem): number | null => {
	if (!assertClipboardHasPlainTextFormat(clipboardItem)) {
		return null;
	}

	if (Number.isNaN(Number(clipboardItem.plainText))) {
		throw new ClientSideValidationError(VALIDATION_ERROR_CODES.NON_COMPATIBLE_FIELD_TYPE);
	}

	return Number(clipboardItem.plainText);
};

const useNumberClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	const [{ value }, { saveValue }] = useNumberField({
		issueKey: issue.key,
		fieldKey: field?.fieldId,
		initialValue: typeof field?.value === 'number' ? field?.value : undefined,
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: typeof value === 'number' ? String(value) : null,
		currentValue: value,
		getPastedValue,
		saveValue,
	});
};

const NumberClipboardHelper = createClipboardHelperComponent(useNumberClipboardHandler);

export const numberClipboardHandler = {
	[NUMBER_CF_TYPE]: NumberClipboardHelper,
	[STORY_POINTS_TYPE]: NumberClipboardHelper,
	[STORY_POINT_ESTIMATE_CF_TYPE]: NumberClipboardHelper,
};
